<template>
  <v-stepper
    class="elevation-0 transparent och-batchEdit__stepper"
    v-model="step"
    v-if="attributeListBase.length"
  >
    <v-stepper-header class="elevation-0">
      <v-stepper-step :complete="step > 1" step="1"
        >Select Edits</v-stepper-step
      >
      <v-divider></v-divider>
      <v-stepper-step :complete="step > 2" step="2"
        >Review Changes</v-stepper-step
      >
      <v-divider></v-divider>
      <v-stepper-step :complete="step > 3" step="3"
        >View Results</v-stepper-step
      >
    </v-stepper-header>
    <v-stepper-items class="remove-left-border">
      <v-stepper-content step="1">
        <v-row>
          <v-col class="col-12 col-xl-6">
            <p>
              You have selected {{ entityName }}s for batch editing. Batch
              editing will allow you to edit specific attributes and apply
              updates to the selected {{ entityName }}s.
            </p>
            <p>
              To review what {{ entityName }}s you have selected for batch
              editing, click the down arrow next to "Currently editing
              {{ entityName }}s".
            </p>
            <p>
              To batch edit the selected {{ entityName }}s, select one or more
              attributes from the down drop list below. The attributes
              selections will appear for editing upon selection. Once you have
              updated the appropriate attribute fields, click the Next button to
              review the changes.
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="col-12 col-md-4 ml-1">
            <v-expansion-panels class="expansion-override">
              <v-expansion-panel class="transparent">
                <v-expansion-panel-header
                  class="font-weight-bold grey--text text--darken-2"
                  >Currently editing {{ idList.length }}
                  {{ entityName }}s</v-expansion-panel-header
                >
                <v-expansion-panel-content>
                  <v-list class="transparent" dense>
                    <v-list-item v-for="entityId in idList" :key="entityId">
                      <v-list-item-content
                        class="body-2 grey--text text--darken-2"
                        >{{ getName(entityId) }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
        <v-row
          ><v-col>
            <label class="och-label"
              >Select an attribute to include in the batch edit.</label
            >
            <v-autocomplete
              @change="updateAttributeListSelected"
              :items="attributeListBatchEdit"
              :search-input.sync="search"
              item-text="label"
              item-value="name"
              outlined
              multiple
              return-object
              v-model="attributeListSelected"
            ></v-autocomplete> </v-col
        ></v-row>
        <v-alert type="info" v-if="blankFieldsExist"
          >Fields selected but left blank will clear the existing value in the
          {{ entityName }} record.
        </v-alert>
        <v-row>
          <v-col>
            <form-entity
              :attributeList="attributeListSelected"
              :entityName="entityName"
              :isBatchEdit="true"
              :primaryKeyName="primaryKeyName"
              @cancel="$router.push({ name: `${entityName}List` })"
              @submitUpdate="step = 2"
              btnTxtCancel="Back"
              btnTxtSubmit="Next"
              v-model="attributeUpdateMap"
            ></form-entity>
          </v-col>
        </v-row>
      </v-stepper-content>
      <v-stepper-content step="2">
        <v-row>
          <v-col class="col-12 col-xl-6">
            <p>
              Please review the changes imported. Items with changes are
              identified in the "Has Changes" column. To review individual
              changes, click the row you would like to review. Once your review
              is complete, click "Submit" to finalize the changes.
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <table-confirm
              :entityList="entityListEdit"
              :entityName="entityName"
              @canceled="step = 1"
              @submitted="
                value => {
                  entityResultList = value;
                  step = 3;
                }
              "
            ></table-confirm>
          </v-col>
        </v-row>
      </v-stepper-content>
      <v-stepper-content step="3">
        <v-row>
          <v-col class="col-12 col-xl-6">
            <p>
              The following updates have been completed. Please review updates
              for any unsuccessful results.
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <table-result
              :entityList="entityResultList"
              :entityName="entityName"
              @canceled="step = 2"
              @completed="
                $router.push({
                  name: redirect,
                })
              "
            ></table-result>
          </v-col>
        </v-row>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import { get, keyBy } from "lodash";
import { mapGetters } from "vuex";

import editFormMixin from "./editFormMixin";

import FormEntity from "./components/Form";
import TableConfirm from "./components/TableConfirm";
import TableResult from "./components/TableResult";

export default {
  components: {
    FormEntity,
    TableConfirm,
    TableResult,
  },
  computed: {
    attributeListBatchEdit() {
      const { attributeListFiltered } = this;

      return attributeListFiltered.filter(
        ({ isEntitySpecific }) => !isEntitySpecific
      );
    },
    entityList() {
      const entityList = this.$store.state[`${this.entityName}`].list;
      return entityList;
    },
    entityMapByEntityId() {
      const { entityList, primaryKeyName } = this;
      const entityMapByEntityId = keyBy(entityList, `${primaryKeyName}`);
      return entityMapByEntityId;
    },
    entityListEdit() {
      const {
        attributeUpdateMap,
        entityMapByEntityId,
        idList,
        importKeyName,
      } = this;

      const entityListEdit = idList.map(entityId => {
        let entity = entityMapByEntityId[entityId];

        if (!entity) {
          return {};
        }

        const importKey = entity[importKeyName];

        return Object.assign({}, attributeUpdateMap, {
          [importKeyName]: importKey,
        });
      });

      return entityListEdit;
    },
    blankFieldsExist() {
      return Object.values(this.attributeUpdateMap).some(
        el => el == undefined || null
      );
    },
    importKey() {
      const { attributeListBase } = this;
      const importKey = attributeListBase.find(
        attribute => !!attribute.importKey
      );
      return importKey;
    },
    importKeyName() {
      const { importKey } = this;

      return get(importKey, "name");
    },
    redirect() {
      return this.$route.query.redirect || `${this.entityName}List`;
    },
    ...mapGetters({
      filterAttribute: "attribute/filter",
    }),
  },
  created() {
    let queryAttributeNameList;
    try {
      queryAttributeNameList = this.$route.query.attributeNameList;

      if (!queryAttributeNameList) {
        return;
      }
      queryAttributeNameList = JSON.parse(queryAttributeNameList);
    } catch (e) {
      console.error(`Could not parse ${queryAttributeNameList} in query!`);
      console.error(e);
      return;
    }
    if (
      !Array.isArray(queryAttributeNameList) ||
      !queryAttributeNameList.length
    ) {
      return;
    }
    // Lots of messy checking, but at this point, we've got an array of attribute names.
    // Now, create our "starting attribute list" via finding batch-editable attributes with names in the list.

    const startingAttributeList = queryAttributeNameList.reduce(
      (acc, attributeName) => {
        const attribute = this.attributeListBatchEdit.find(
          attribute => attribute.name === attributeName
        );

        if (attribute) {
          acc.push(attribute);
        }
        return acc;
      },
      []
    );

    this.attributeListSelected = startingAttributeList;
  },
  data() {
    return {
      attributeListSelected: [],
      attributeUpdateMap: {},
      step: 1,
      entityResultList: [],
      search: null,
    };
  },
  methods: {
    getName(entityId) {
      const { entityMapByEntityId, importKeyName } = this;

      const entity = entityMapByEntityId[entityId];

      if (!entity) {
        return "--";
      }

      let { name, label } = entity;
      const importKey = entity[importKeyName];

      if (!label) {
        label = name;
      }

      if (!label) {
        return importKey;
      }

      return `${label} (${importKey})`;
    },
    updateAttributeListSelected(attributeListSelected) {
      const attributeNameListSelected = attributeListSelected.map(attribute => {
        return attribute.name;
      });

      this.attributeUpdateMap = attributeNameListSelected.reduce(
        (acc, attributeName) => {
          const attributeValue = this.attributeUpdateMap[attributeName];

          if (!attributeValue) {
            acc[attributeName] = null;
            return acc;
          }
          acc[attributeName] = attributeValue;
          return acc;
        },
        {}
      );
    },
  },
  mixins: [editFormMixin],
  props: {
    idList: {
      required: true,
      type: Array,
    },
  },
};
</script>

<style scoped>
.expansion-override {
  margin-left: -25px;
}
.och-batchEdit__stepper .v-expansion-panel:before {
  box-shadow: none !important;
}
.och-batchEdit__stepper .v-stepper__content {
  border-left: none;
  margin-left: 0;
  padding-left: 0 !important;
}
.och-batchEdit__stepper .v-stepper__step:first-child {
  padding-left: 0 !important;
}
</style>
